//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { isDefined } from '@utils/filterPredicates';

interface SessionItemKeyTypes {
  'sidebar-scroll-position': number;
}

type SessionItemKeys = keyof SessionItemKeyTypes;
type SessionItemType<K extends SessionItemKeys> = SessionItemKeyTypes[K];

interface Key<K extends SessionItemKeys> {
  key: K;
  keyPostfix?: string;
}

function makeKey<K extends SessionItemKeys>(key: Key<K>) {
  return `${key.key}${isDefined(key.keyPostfix) ? `-${key.keyPostfix}` : ''}`;
}

interface SessionValue<K extends SessionItemKeys> {
  value: SessionItemType<K>;
  createdTimeMillis: number;
  expiresInMillis?: number;
}

/**
 * Get session storage item with proper validation
 * Returns null when item doesn't exist or fails validation
 */
export function getSessionItem<K extends SessionItemKeys>(
  key: Key<K>,
): SessionItemType<K> | null {
  const item = sessionStorage.getItem(makeKey(key));

  if (item === null) {
    return null;
  }

  try {
    const { createdTimeMillis, expiresInMillis, value } = JSON.parse(
      item,
    ) as SessionValue<K>;

    if (
      isDefined(expiresInMillis) &&
      createdTimeMillis + expiresInMillis < Date.now()
    ) {
      sessionStorage.removeItem(makeKey(key));
      return null;
    }
    return value;
  } catch {
    console.warn(
      `[Session Storage] -> Invalid value for ${makeKey(key)}: ${item}`,
    );
    return null;
  }
}

/**
 * Set session storage item with strict validation
 */
export function setSessionItem<K extends SessionItemKeys>({
  expiresInMillis,
  key,
  value,
}: {
  key: Key<K>;
  value: SessionItemType<K>;
  expiresInMillis?: number;
}): void {
  const fullValue: SessionValue<K> = {
    value,
    createdTimeMillis: Date.now(),
    expiresInMillis: expiresInMillis,
  };
  sessionStorage.setItem(makeKey(key), JSON.stringify(fullValue));
}

/**
 * Removes all session items with the given prefix, except the one with the given postfix
 */
export function removeSessionItems({
  postfixToKeep,
  prefix,
}: {
  prefix: SessionItemKeys;
  postfixToKeep?: string;
}): void {
  for (const key of Object.keys(sessionStorage)) {
    if (
      key.startsWith(prefix) &&
      (!isDefined(postfixToKeep) || !key.endsWith(postfixToKeep))
    ) {
      sessionStorage.removeItem(key);
    }
  }
}

/**
 * Clear all session storage
 */
export function clearSession(): void {
  sessionStorage.clear();
}
